<template>
    <article class="article">
        <AppBreadcrumbs
            :base-url="runtimeConfig.public.BASE_URL"
            :parents="[
                {
                    name: config.brand_name,
                    full_slug: '',
                },
                ...parentPages,
            ]"
            :current="{
                name: article.name,
                full_slug: article.full_slug,
            }"
        />
        <div class="article__main">
            <div class="article__content">
                <WeatherHeader
                    :location="article.content.location"
                    :month="parseInt(article.content.month)"
                    :image="article.content.image.filename"
                />

                <StoryblokComponent
                    v-for="contentBlok in article.content.content"
                    :key="contentBlok._uid"
                    :month="parseInt(article.content.month)"
                    :blok="contentBlok"
                />

                <infographic-canvas
                    style="max-width: 600px;margin-top: 1rem;"
                    :name="`weather-overview-${article.content.location || 'Tanzania'}-${parseInt(article.content.month) + 1}`.trim()"
                    :alt="`Weather overview for ${article.content.location || 'Tanzania'} in ${monthInString}`"
                >
                    <infographic-key-value-list>
                        <template #header>
                            {{ article.content.location || 'Tanzania' }} Weather in {{ monthInString }}
                        </template>

                        <infographic-key-value-item
                            v-for="item in infographicItems"
                            :key="item.key"
                        >
                            <template #key>
                                {{ item.key }}
                            </template>
                            <template #value>
                                {{ item.value }}
                            </template>
                        </infographic-key-value-item>
                    </infographic-key-value-list>
                </infographic-canvas>
            </div>
            <div class="article__sidebar">
                <StoryblokComponent
                    v-for="contentBlok in article.content.sidebar"
                    :key="contentBlok._uid"
                    :story="article"
                    :blok="contentBlok"
                />
            </div>
        </div>

        <StaticFeaturedArticles
            title="Explore <span class='u-highlight'>our</span> guides!"
            text="Made by locals, to help you make the most of your trip."
            :articles="recommendedArticles"
        />
    </article>
</template>

<script setup>
import StaticFeaturedArticles from '~/components/featured-articles/static-featured-articles.vue'
import { buildBreadcrumbParents } from '~/helpers/breadcrumb-parents'
import { getPrecipitationDataOfMonth } from '~/helpers/precipitation-data'
import seoTitle from '~/helpers/seoTitle'
import { getTemperatureDataOfMonth } from '~/helpers/temperature-data'
import WeatherHeader from '~/storyblok/weather-header/weather-header.vue'

const { data } = defineProps({
    data: {
        type: Object,
        required: true,
    },
})
const route = useRoute()
const { slug } = route.params
const { capture } = useTracking()

const parentPages = computed(() => buildBreadcrumbParents(slug))

// Recursively walk the content tree and substitute `{{token}}` inside string
// leaves. Values arrive pre-serialized; returns a structurally-shared copy
// (objects/arrays are only recreated along paths that contain a token).
function interpolateTokens(node, values) {
    if (typeof node === 'string') {
        return node.replace(/\{\{(\w+)\}\}/g, (match, key) => (key in values ? values[key] : match))
    }
    if (Array.isArray(node)) {
        return node.map(child => interpolateTokens(child, values))
    }
    if (node && typeof node === 'object') {
        const out = {}
        for (const [key, value] of Object.entries(node)) {
            out[key] = interpolateTokens(value, values)
        }
        return out
    }
    return node
}

const { data: config } = await useConfig()

const month = Number.parseInt(data.article.content.month) + 1
const temperatureData = await getTemperatureDataOfMonth(month)
const precipitationData = await getPrecipitationDataOfMonth(month)

// Pre-serialize every token value to a string up-front (some helpers return
// numbers, e.g. rainy_days_number), then substitute `{{token}}` only inside
// the text nodes of the article content tree. This replaces the old
// JSON.stringify → regex-replace → JSON.parse round-trip, which re-serialized
// the entire article object (and ran 30+ global regexes over it) per render.
const tokens = { ...temperatureData, ...precipitationData }
const serialized = {}
for (const [key, value] of Object.entries(tokens)) {
    serialized[key] = value == null ? '' : String(value)
}

const article = {
    ...data.article,
    content: interpolateTokens(data.article.content, serialized),
}
const { recommendedArticles } = data

const monthInString = new Date(2024, Number.parseInt(article.content.month), 1).toLocaleString('en-US', { month: 'long' })
const runtimeConfig = useRuntimeConfig()

onMounted(() => {
    capture('weather_viewed', {
        location: article.content.location || 'Tanzania',
        month: monthInString,
    })
})

const infographicItems = [
    {
        key: 'Average temperature',
        value: `${temperatureData.temperature_celsius} (${temperatureData.temperature_fahrenheit})`,
    },
    {
        key: 'Min temperature',
        value: `${temperatureData.min_temperature_celsius} (${temperatureData.min_temperature_fahrenheit})`,
    },
    {
        key: 'Max temperature',
        value: `${temperatureData.max_temperature_celsius} (${temperatureData.max_temperature_fahrenheit})`,
    },
    {
        key: 'Precipitation',
        value: `${precipitationData.precipitation}mm`,
    },
    {
        key: 'Rainy days',
        value: precipitationData.rainy_days_number,
    },
]

useHead({
    title: seoTitle(article),
    meta: [
        {
            hid: 'description',
            name: 'description',
            content: article.content.meta_description,
        },
    ],
})
</script>
